import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Collapse } from 'react-collapse';
import { BodyLink, Button } from '@kvdbil/components';
import { ExplanationText } from '~/App/views/ObjectPage/components/ExplanationText';
import { useTranslation } from '~/Locale';
import { AuctionType } from '~/App/shared/interfaces/Auction';
import { ProcessedAuction } from '~/App/shared/interfaces/ProcessedAuction';
import { useDispatch, useSelector } from 'react-redux';
import { openBiddingView } from '~/App/shared/actions/objectViewDrawerActions';
import { Info } from '~/App/shared/components/IconButtons/Info';
import { isObjectViewDrawerOpen } from '~/App/shared/selectors/storeObject';

const BiddingButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ResPriceExplanation = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const ButtonWrapper = styled.span`
  width: 100%;
  text-align: center;
  margin-top: 0.5rem;

  button {
    height: 3rem;
  }
`;

interface Props {
  auction: ProcessedAuction;
  isClosed: boolean;
}

const BiddingButton = ({ auction, isClosed }: Props) => {
  const [resPriceExplanationIsVisible, toggleResPriceExplanationIsVisibility] =
    useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isBiddingOpen = useSelector(isObjectViewDrawerOpen);

  useEffect(() => {
    toggleResPriceExplanationIsVisibility(false);
  }, [auction.id]);

  const reservationPriceReached =
    auction.activeAuction?.reservationPriceReached;
  const numberOfBids = auction.bids?.length ?? 0;

  const getButtonLabel = () => {
    if (isClosed) {
      return t('See the result');
    }
    if (numberOfBids > 0) {
      const bidWord = numberOfBids === 1 ? t('Bid') : t('Bids');
      return `${t('Open bidding')} - ${numberOfBids} ${bidWord}`;
    }
    return t('Open bidding');
  };

  return (
    <BiddingButtonContainer>
      <ResPriceExplanation>
        <BodyLink as="span">
          {reservationPriceReached
            ? t('Reserve price is reached')
            : t('Reserve price is not reached')}
        </BodyLink>
        <Info
          isInfoOpened={resPriceExplanationIsVisible}
          onClick={() => toggleResPriceExplanationIsVisibility(prev => !prev)}
          ariaControls="res-price-info-content"
        />
      </ResPriceExplanation>
      <Collapse isOpened={resPriceExplanationIsVisible}>
        <ExplanationText id="res-price-info-content">
          {t('Reservation price explanation')}
        </ExplanationText>
      </Collapse>

      <ButtonWrapper>
        <Button
          color="secondary"
          size="large"
          type="button"
          fullWidth
          onClick={() => {
            dispatch(
              openBiddingView(auction.id, AuctionType.BIDDING, 'default')
            );
          }}
          aria-haspopup="dialog"
          aria-expanded={isBiddingOpen}
          aria-controls="object-bid-reserve-drawer"
        >
          {getButtonLabel()}
        </Button>
      </ButtonWrapper>
    </BiddingButtonContainer>
  );
};

export default BiddingButton;
